import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },
  getMe: () => api.get('/api/auth/me'),
};

// Applications endpoints
export const appsAPI = {
  list: () => api.get('/api/apps'),
  create: (data) => api.post('/api/apps', data),
  get: (id) => api.get(`/api/apps/${id}`),
  update: (id, data) => api.put(`/api/apps/${id}`, data),
  delete: (id) => api.delete(`/api/apps/${id}`),
};

// Test cases endpoints
export const testCasesAPI = {
  list: (appId, status) => {
    const params = status ? { status_filter: status } : {};
    return api.get(`/api/apps/${appId}/testcases`, { params });
  },
  create: (appId, data) => api.post(`/api/apps/${appId}/testcases`, data),
  get: (testId) => api.get(`/api/testcases/${testId}`),
  getLogs: (testId) => api.get(`/api/testcases/${testId}/logs`),
  downloadScript: (testId) => api.get(`/api/testcases/${testId}/script`, { responseType: 'blob' }),
  viewScript: (testId) => api.get(`/api/testcases/${testId}/script/view`),
  runTest: (testId) => api.post(`/api/testcases/${testId}/run`),
  retry: (testId) => api.post(`/api/testcases/${testId}/retry`),
};

// Public endpoints
export const publicAPI = {
  getModels: () => api.get('/api/models/public'),
  healthCheck: () => api.get('/api/health'),
};

// Admin endpoints
export const adminAPI = {
  // Users
  listUsers: () => api.get('/api/admin/users'),
  getUser: (id) => api.get(`/api/admin/users/${id}`),
  updateCredits: (id, amount, reason) => api.put(`/api/admin/users/${id}/credits`, { amount, reason }),
  toggleUserActive: (id) => api.put(`/api/admin/users/${id}/toggle-active`),
  getUserTransactions: (id) => api.get(`/api/admin/users/${id}/transactions`),

  // AI Models
  listModels: () => api.get('/api/admin/models'),
  createModel: (data) => api.post('/api/admin/models', data),
  updateModel: (id, data) => api.put(`/api/admin/models/${id}`, data),
  deleteModel: (id) => api.delete(`/api/admin/models/${id}`),
};

export default api;
